import React from 'react';
import QRCode from 'react-qr-code';

interface QRCodeDisplayProps {
  value: string;
  size?: number;
  className?: string;
  showLabel?: boolean;
  label?: string;
}

/**
 * QR Code Display Component
 * 
 * This is a self-contained QR code display component that can be easily
 * removed without affecting core functionality. All QR-related features
 * are contained within the /components/qr/ directory.
 * 
 * To remove QR functionality:
 * 1. Delete src/components/qr/ directory
 * 2. Remove imports from parent components
 * 3. Uninstall qrcode.react package
 */
export function QRCodeDisplay({
  value,
  size = 128,
  className = '',
  showLabel = false,
  label = 'Scan Here'
}: QRCodeDisplayProps) {
  return (
    <div className={`flex flex-col items-center space-y-2 ${className}`}>
      <div className="bg-white p-2 rounded-lg border border-gray-200">
        <QRCode
          value={value}
          size={size}
          level="H"
          bgColor="#FFFFFF"
          fgColor="#000000"
        />
      </div>
      {showLabel && (
        <span className="text-xs text-gray-500 text-center">{label}</span>
      )}
    </div>
  );
}

/**
 * Generate QR code value for shipment
 * Format: "shipment:SHP-001"
 */
export function generateShipmentQRValue(shipmentNumber: string): string {
  return `shipment:${shipmentNumber}`;
}

/**
 * Generate QR code value for invoice
 * Format: "invoice:INV-001"
 */
export function generateInvoiceQRValue(invoiceNumber: string): string {
  return `invoice:${invoiceNumber}`;
}

/**
 * Generate QR code value for sourcing request
 * Format: "request:REQ-001"
 */
export function generateSourcingRequestQRValue(request: any): string {
  const requestNumber = request.requestNumber || request.request_number || '';
  return `request:${requestNumber}`;
}

/**
 * Parse QR code value to extract type and identifier
 * Returns { type: 'shipment' | 'invoice' | 'request', id: string } or null if invalid
 */
export function parseQRValue(qrValue: string): { type: 'shipment' | 'invoice' | 'request'; id: string } | null {
  if (qrValue.startsWith('shipment:')) {
    return { type: 'shipment', id: qrValue.replace('shipment:', '') };
  }
  if (qrValue.startsWith('invoice:')) {
    return { type: 'invoice', id: qrValue.replace('invoice:', '') };
  }
  if (qrValue.startsWith('request:')) {
    return { type: 'request', id: qrValue.replace('request:', '') };
  }
  return null;
}